import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer } from "./ui";
import DemoRequestInfoSection from "./DemoRequestInfoSection";
import DemoRequestQuoteSection from "./DemoRequestQuoteSection";
import DemoRequestSchedulingSection from "./DemoRequestSchedulingSection";
import DemoRequestNotesSection from "./DemoRequestNotesSection";

export default function DemoRequestDrawer({ request, open, onClose, onUpdated }) {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(request);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [schedulingMode, setSchedulingMode] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [quoteCreating, setQuoteCreating] = useState(false);

  useEffect(() => {
    setCurrent(request);
    setNote("");
    setError("");
    setSchedulingMode(null);
    setCancelConfirmOpen(false);
  }, [request, open]);

  const onScheduled = (updatedRequest) => {
    setCurrent(updatedRequest);
    setSchedulingMode(null);
    onUpdated?.(updatedRequest);
  };

  const cancelDemo = async () => {
    setCancelSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/demo-requests/${current.demo_request_id}/schedule/cancel`,
        {},
        { withCredentials: true }
      );
      setCurrent(data.demo_request);
      onUpdated?.(data.demo_request);
      setCancelConfirmOpen(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelSaving(false);
    }
  };

  const saveStatus = async (status) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(
        `${API}/admin/demo-requests/${current.demo_request_id}/status`,
        { status },
        { withCredentials: true }
      );
      setCurrent(data.demo_request);
      onUpdated?.(data.demo_request);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const goToQuote = (quoteId) => {
    onClose();
    navigate(`/admin/quotes?focus=${quoteId}`);
  };

  const createQuote = async () => {
    setQuoteCreating(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/demo-requests/${current.demo_request_id}/create-quote`, {}, { withCredentials: true });
      goToQuote(data.quote.quote_id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setQuoteCreating(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/demo-requests/${current.demo_request_id}/notes`,
        { body: note.trim() },
        { withCredentials: true }
      );
      setCurrent(data.demo_request);
      setNote("");
      onUpdated?.(data.demo_request);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={current?.company || "Demo Request"}
      subtitle={current ? `${current.name} · ${current.email}` : ""}
      testId="demo-request-drawer"
    >
      {current && (
        <div className="space-y-7">
          <DemoRequestInfoSection current={current} saving={saving} onStatusChange={saveStatus} />

          <DemoRequestQuoteSection
            current={current}
            quoteCreating={quoteCreating}
            onCreateQuote={createQuote}
            onViewQuote={() => goToQuote(current.quote_id)}
          />

          <DemoRequestSchedulingSection
            current={current}
            schedulingMode={schedulingMode}
            setSchedulingMode={setSchedulingMode}
            onScheduled={onScheduled}
            cancelConfirmOpen={cancelConfirmOpen}
            setCancelConfirmOpen={setCancelConfirmOpen}
            cancelDemo={cancelDemo}
            cancelSaving={cancelSaving}
            error={error}
          />

          <DemoRequestNotesSection
            notes={current.admin_notes}
            note={note}
            setNote={setNote}
            saving={saving}
            error={error}
            onAddNote={addNote}
          />
        </div>
      )}
    </DetailDrawer>
  );
}
